import { Task } from '../../types';
import { getTaskStartDate, getTaskStartDateValue } from '../../utils/taskSchedule';

export { getTaskStartDate, getTaskStartDateValue, isStartSchedule } from '../../utils/taskSchedule';

export const TIMELINE_DEADLINE_DURATION_MINUTES = 30;

// Older tasks created with the collection date picker have a midnight date and
// no duration. Treat those as all-day too, so existing user data keeps the
// intended meaning without a migration.
export const isAllDayTask = (task: Task) => {
    if (task.schedule) return false;
    const start = getTaskStartDate(task);
    if (!start || task.duration !== undefined) return false;

    return start.getHours() === 0 && start.getMinutes() === 0 && start.getSeconds() === 0;
};

export const isTimelineAllDayTask = (task: Task) => {
    if (isAllDayTask(task)) return true;
    const start = getTaskStartDate(task);
    if (!start) return false;
    return getTaskEnd(task).getTime() - start.getTime() > 24 * 60 * 60_000;
};

export const isHourlySchedule = (task: Task) => {
    const start = getTaskStartDateValue(task);
    if (!task.schedule || !start) return false;
    if (task.schedule.precision === 'hour') return true;
    const hasTime = (value: string) => {
        const date = new Date(value);
        return date.getHours() !== 0 || date.getMinutes() !== 0;
    };
    return task.schedule.precision === 'day'
        && (hasTime(start) || (task.schedule.endDate ? hasTime(task.schedule.endDate) : false));
};

export type FlexiblePrecision = 'day' | 'month' | 'year';

export const getFlexiblePrecision = (task: Task): FlexiblePrecision | undefined => {
    if (isHourlySchedule(task)) return undefined;
    if (task.schedule?.precision && task.schedule.precision !== 'hour') return task.schedule.precision;
    return isAllDayTask(task) ? 'day' : undefined;
};

export const getTaskEnd = (task: Task) => {
    const start = getTaskStartDate(task);
    if (!start) return new Date(NaN);
    return task.schedule?.precision === 'hour' && task.schedule.endDate
        ? new Date(task.schedule.endDate) : new Date(start.getTime() + (task.duration || 60) * 60_000);
};

export const getTimelineTaskRange = (task: Task) => {
    const start = getTaskStartDate(task);
    if (!start) return { start: new Date(NaN), end: new Date(NaN) };

    if (task.schedule?.kind === 'deadline' && task.schedule.precision === 'hour') {
        const end = task.schedule.endDate ? new Date(task.schedule.endDate) : start;
        return {
            start: new Date(end.getTime() - TIMELINE_DEADLINE_DURATION_MINUTES * 60_000),
            end,
        };
    }

    return { start, end: getTaskEnd(task) };
};

export const getTaskColorClass = (task: Task) => {
    const precision = getFlexiblePrecision(task);
    return precision ? `task-${precision}` : 'task-timed';
};

export const getFlexibleRange = (task: Task) => {
    const start = getTaskStartDate(task) || new Date(NaN);
    const end = task.schedule?.endDate ? new Date(task.schedule.endDate) : new Date(start);
    return start <= end ? { start, end } : { start: end, end: start };
};

export const normalizeFlexibleDate = (date: Date, precision: NonNullable<Task['schedule']>['precision']) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);

    if (precision === 'month') {
        normalized.setDate(1);
    } else if (precision === 'year') {
        normalized.setMonth(0, 1);
    }

    return normalized;
};
